<template>
<div>
    <p class="text-muted small">Demerara Mutual — Health Claim Form. Submit within 90 days.</p>
    <b-alert show variant="warning" class="small mb-3">
        Note: this PDF is a scanned image (no text layer), so field positions
        couldn't be auto-detected. Generated text may need manual alignment
        — adjust <code>buildCoords()</code> in <code>DemeraraMutualForm.vue</code>.
    </b-alert>

    <b-form @submit.prevent="onSubmit" novalidate>
        <h6>Policyholder / employer</h6>
        <b-row>
            <b-col md="4"><b-form-group label="Policy No."><b-form-input v-model="form.policy_no" /></b-form-group></b-col>
            <b-col md="4"><b-form-group label="Employer / policyholder"><b-form-input v-model="form.employer" /></b-form-group></b-col>
            <b-col md="4"><b-form-group label="Certificate No."><b-form-input v-model="form.cert_no" /></b-form-group></b-col>
        </b-row>

        <h6>Insured / patient</h6>
        <b-row>
            <b-col md="6">
                <b-form-group label="Employee / insured name"><b-form-input v-model="form.employee_name" /></b-form-group>
                <b-form-group label="Address"><b-form-textarea rows="2" v-model="form.address" /></b-form-group>
                <b-form-group label="Telephone"><b-form-input v-model="form.phone" /></b-form-group>
            </b-col>
            <b-col md="6">
                <b-form-group label="Patient name"><b-form-input v-model="form.patient_name" /></b-form-group>
                <b-form-group label="Date of birth"><b-form-datepicker v-model="form.dob" /></b-form-group>
                <b-form-group label="Relationship"><b-form-input v-model="form.relationship" placeholder="Self / Spouse / Child..." /></b-form-group>
                <b-form-group label="Spouse's employer (if applicable)"><b-form-input v-model="form.spouse_employer" /></b-form-group>
            </b-col>
        </b-row>

        <h6>Condition details</h6>
        <b-row>
            <b-col md="4"><b-form-group label="Employment-related"><b-form-radio-group v-model="form.is_employment" :options="yesNoOpts" /></b-form-group></b-col>
            <b-col md="4"><b-form-group label="Auto accident"><b-form-radio-group v-model="form.is_auto" :options="yesNoOpts" /></b-form-group></b-col>
            <b-col md="4"><b-form-group label="Other accident"><b-form-radio-group v-model="form.is_other_accident" :options="yesNoOpts" /></b-form-group></b-col>
        </b-row>
        <b-form-group label="If YES — details"><b-form-textarea rows="2" v-model="form.accident_details" /></b-form-group>
        <b-form-group label="Other coverage? Insurance / company name"><b-form-input v-model="form.other_coverage" /></b-form-group>

        <h6>Doctor / health provider</h6>
        <b-row>
            <b-col md="6">
                <b-form-group label="Doctor name &amp; address"><b-form-textarea rows="2" v-model="form.doctor_name_address" /></b-form-group>
                <b-form-group label="Referring physician"><b-form-input v-model="form.referring_physician" /></b-form-group>
            </b-col>
            <b-col md="6">
                <b-form-group label="Diagnosis 1"><b-form-input v-model="form.diagnosis_1" /></b-form-group>
                <b-form-group label="Diagnosis 2"><b-form-input v-model="form.diagnosis_2" /></b-form-group>
                <b-form-group label="Pregnancy related?"><b-form-radio-group v-model="form.is_pregnancy" :options="yesNoOpts" /></b-form-group>
                <b-form-group label="Date of last monthly period"><b-form-datepicker v-model="form.lmp_date" /></b-form-group>
            </b-col>
        </b-row>

        <h6>Treatment</h6>
        <b-row>
            <b-col md="6">
                <b-form-group label="Date of first symptoms"><b-form-datepicker v-model="form.first_symptoms" /></b-form-group>
                <b-form-group label="Date of first consultation"><b-form-datepicker v-model="form.first_consult" /></b-form-group>
                <b-form-group label="Previously treated for this condition?"><b-form-radio-group v-model="form.prev_treated" :options="yesNoOpts" /></b-form-group>
                <b-form-group label="If yes — date"><b-form-datepicker v-model="form.prev_treated_date" /></b-form-group>
            </b-col>
            <b-col md="6">
                <b-form-group label="Surgical procedure (type)"><b-form-input v-model="form.procedure_type" /></b-form-group>
                <b-form-group label="Date of operation"><b-form-datepicker v-model="form.op_date" /></b-form-group>
                <b-form-group label="Surgeon"><b-form-input v-model="form.surgeon" /></b-form-group>
                <b-form-group label="Total charges"><b-form-input v-model="form.total_charges" /></b-form-group>
            </b-col>
        </b-row>

        <div class="text-right mt-3">
            <b-button variant="secondary" @click="$emit('close')">Cancel</b-button>
            <b-button type="submit" variant="success">
                <i class="fa-solid fa-file-arrow-down"></i> Generate PDF
            </b-button>
        </div>
    </b-form>
</div>
</template>

<script>
import { formatDate, buildAddress, bestPhone, fullName, loadCoords } from "./pdfFiller"
import formPersistence from "./formPersistence"

export default {
    name: "demerara-mutual-form",
    mixins: [formPersistence],
    props: { demographics: { type: Object, default: () => ({}) } },
    data() {
        return {
            yesNoOpts: [{ value: "yes", text: "Yes" }, { value: "no", text: "No" }],
            form: {
                policy_no: "", employer: "", cert_no: "",
                employee_name: "", address: "", phone: "",
                patient_name: "", dob: "", relationship: "Self", spouse_employer: "",
                is_employment: "no", is_auto: "no", is_other_accident: "no",
                accident_details: "", other_coverage: "",
                doctor_name_address: "", referring_physician: "",
                diagnosis_1: "", diagnosis_2: "",
                is_pregnancy: "no", lmp_date: "",
                first_symptoms: "", first_consult: "",
                prev_treated: "no", prev_treated_date: "",
                procedure_type: "", op_date: "", surgeon: "", total_charges: "",
            },
        }
    },
    watch: {
        demographics: {
            immediate: true,
            handler(d) {
                if (!d) return
                const name = fullName(d)
                if (!this.form.employee_name) this.form.employee_name = name
                if (!this.form.patient_name) this.form.patient_name = name
                if (!this.form.dob) this.form.dob = d.date_of_birth || ""
                if (!this.form.address) this.form.address = buildAddress(d)
                if (!this.form.phone) this.form.phone = bestPhone(d)
                if (!this.form.employer && d.employer) this.form.employer = d.employer?.name || d.employer
                if (!this.form.doctor_name_address && d.primary_physician) {
                    this.form.doctor_name_address = [
                        d.primary_physician.name,
                        d.primary_physician.address
                    ].filter(Boolean).join("\n")
                }
                if (!this.form.referring_physician && d.primary_physician?.name) {
                    this.form.referring_physician = d.primary_physician.name
                }
            },
        },
    },
    methods: {
        buildCoords() {
            // Coords measured off the rendered Demerara PDF using a grid
            // overlay. Each value y is the BASELINE — placed just below the
            // corresponding label so text sits in the value area of the cell.
            // Coordinates verified against the rendered page 0 (612x792).
            // Derived by rendering the scanned template to an image, measuring
            // each label's pixel position and converting (pt = px * 0.48,
            // y = 792 - py * 0.48), then confirming with a fill+render loop.
            return {
                // Section 1 — Employer / Policyholder
                policy_no: { page: 0, x: 48, y: 632 },
                employer:  { page: 0, x: 187, y: 632 },
                admin_sig: { page: 0, x: 760, y: 632 },
                cert_no:   { page: 0, x: 48, y: 618 },
                date_top:  { page: 0, x: 367, y: 618 },

                // Section 2 — Employee / Insured
                employee_name:   { page: 0, x: 48, y: 590 },
                patient_name:    { page: 0, x: 205, y: 593 },
                spouse_employer: { page: 0, x: 431, y: 590 },
                relationship:    { page: 0, x: 318, y: 577 },
                dob:             { page: 0, x: 247, y: 577 },
                address:         { page: 0, x: 48, y: 572 },

                // Condition checkboxes
                is_employment_yes: { page: 0, x: 356, y: 570, type: "check" },
                is_employment_no:  { page: 0, x: 382, y: 570, type: "check" },
                is_auto_yes: { page: 0, x: 266, y: 564, type: "check" },
                is_auto_no:  { page: 0, x: 293, y: 564, type: "check" },
                is_other_yes: { page: 0, x: 355, y: 564, type: "check" },
                is_other_no:  { page: 0, x: 382, y: 564, type: "check" },

                phone: { page: 0, x: 94, y: 556 },
                accident_details: { page: 0, x: 250, y: 552 },

                // Is patient covered by other plans? Yes/No
                other_yes: { page: 0, x: 350, y: 539, type: "check" },
                other_no:  { page: 0, x: 372, y: 539, type: "check" },
                other_coverage: { page: 0, x: 211, y: 529 },
                other_group:    { page: 0, x: 250, y: 515 },

                // Section 3 — Doctor / Health Provider
                doctor_patient_name: { page: 0, x: 48, y: 376 },
                doctor_name_address: { page: 0, x: 304, y: 376 },
                diagnosis_1: { page: 0, x: 74, y: 350 },
                diagnosis_2: { page: 0, x: 202, y: 350 },
                diagnosis_3: { page: 0, x: 74, y: 336 },
                diagnosis_4: { page: 0, x: 202, y: 336 },
                referring_physician: { page: 0, x: 304, y: 334 },

                is_pregnancy_yes: { page: 0, x: 124, y: 322, type: "check" },
                is_pregnancy_no:  { page: 0, x: 149, y: 322, type: "check" },
                lmp_date: { page: 0, x: 307, y: 322 },

                // Section 4 — Medical Doctor / Surgeon
                first_symptoms: { page: 0, x: 122, y: 292 },
                prev_treated_yes: { page: 0, x: 434, y: 293, type: "check" },
                prev_treated_no:  { page: 0, x: 461, y: 293, type: "check" },
                first_consult: { page: 0, x: 152, y: 278 },
                prev_treated_date: { page: 0, x: 341, y: 278 },

                // Surgical procedure block
                op_date:        { page: 0, x: 307, y: 170 },
                procedure_type: { page: 0, x: 307, y: 157 },
                surgeon:        { page: 0, x: 307, y: 143 },
                assistant:      { page: 0, x: 307, y: 129 },
                total_charges:  { page: 0, x: 518, y: 129 },
            }
        },
        buildValues() {
            const f = this.form
            return {
                // Section 1
                policy_no: f.policy_no,
                employer: f.employer,
                admin_sig: "",
                cert_no: f.cert_no,
                date_top: formatDate(new Date()),
                // Section 2
                employee_name: f.employee_name,
                patient_name: f.patient_name,
                spouse_employer: f.spouse_employer,
                relationship: f.relationship,
                address: f.address,
                dob: formatDate(f.dob),
                phone: f.phone,
                is_employment_yes: f.is_employment === "yes",
                is_employment_no: f.is_employment === "no",
                is_auto_yes: f.is_auto === "yes",
                is_auto_no: f.is_auto === "no",
                is_other_yes: f.is_other_accident === "yes",
                is_other_no: f.is_other_accident === "no",
                accident_details: f.accident_details,
                other_yes: !!f.other_coverage,
                other_no: !f.other_coverage,
                other_coverage: f.other_coverage,
                other_group: "",
                // Section 3
                doctor_patient_name: f.patient_name,
                doctor_name_address: f.doctor_name_address,
                diagnosis_1: f.diagnosis_1,
                diagnosis_2: f.diagnosis_2,
                diagnosis_3: "",
                diagnosis_4: "",
                referring_physician: f.referring_physician,
                is_pregnancy_yes: f.is_pregnancy === "yes",
                is_pregnancy_no: f.is_pregnancy === "no",
                lmp_date: formatDate(f.lmp_date),
                // Section 4
                first_symptoms: formatDate(f.first_symptoms),
                first_consult: formatDate(f.first_consult),
                prev_treated_yes: f.prev_treated === "yes",
                prev_treated_no: f.prev_treated === "no",
                prev_treated_date: formatDate(f.prev_treated_date),
                op_date: formatDate(f.op_date),
                procedure_type: f.procedure_type,
                surgeon: f.surgeon,
                assistant: "",
                total_charges: f.total_charges,
            }
        },
        async onSubmit() {
            try {
                // Demerara's blank PDF is a scanned image with no text layer, so
                // auto-extraction can't produce a coords JSON. We always fall
                // back to the hardcoded estimates here — adjust buildCoords()
                // by hand to align with the form.
                const coords = (await loadCoords("demerara-mutual-health-claim")) || this.buildCoords()
                await this.fillAndPersist({ form_key: "demerara-mutual", title: "Demerara Mutual Health Claim" }, {
                    templateUrl: "/forms/demerara-mutual-health-claim.pdf",
                    outputName: `demerara-mutual-${this.form.patient_name || "patient"}.pdf`,
                    values: this.buildValues(),
                    coords,
                })
                this.$emit("close")
            } catch (e) {
                this.$bvToast.toast(e.message || "Could not generate PDF", {
                    title: "PDF generation failed",
                    variant: "danger",
                })
            }
        },
    },
}
</script>
